import { CheckCircleIcon, CircleIcon } from 'lucide-react-native';
import { useEffect, useState } from 'react';

import { progressStepState, progressSteps, SLOW_RUN_AFTER_MS } from '../../model/progress';
import type { NpiSuggestionStage } from '../../model/types';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

const STATE_LABEL = {
  done: 'done',
  active: 'in progress',
  pending: 'not started',
} as const;

/**
 * Shows which step of finding the person's NPI the server is on, so a slow
 * answer reads as progress rather than as "nothing found". Each step says its
 * state in words for a screen reader, not by icon alone, and the current step
 * is announced as it changes. After a while it says so, and points at the NPI
 * field below, which stays usable the whole time: nothing here ever times out
 * on the person.
 */
export function NpiSuggestionProgress({
  stage,
  fromTypedName = false,
}: {
  stage: NpiSuggestionStage | null;
  /** The run searches a name the person typed rather than reading their email. */
  fromTypedName?: boolean;
}) {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_RUN_AFTER_MS);

    return () => clearTimeout(timer);
  }, []);

  return (
    <VStack className="w-full gap-2 py-1" testID="npi-suggestions-progress">
      <Text size="sm" className="font-semibold text-typography-900">
        Finding your NPI
      </Text>
      {progressSteps(fromTypedName).map((step) => {
        const state = progressStepState(step.stage, stage);

        return (
          <HStack
            key={step.stage}
            className="items-center gap-2"
            accessibilityLiveRegion={state === 'active' ? 'polite' : 'none'}
            {...getAccessibilityProps({ label: `${step.label}, ${STATE_LABEL[state]}` })}>
            {state === 'done' ? (
              <Icon as={CheckCircleIcon} className="h-4 w-4 text-success-700" />
            ) : null}
            {state === 'active' ? <Spinner size="small" /> : null}
            {state === 'pending' ? (
              <Icon as={CircleIcon} className="h-4 w-4 text-typography-500" />
            ) : null}
            <Text
              size="sm"
              className={state === 'pending' ? 'text-typography-600' : 'text-typography-900'}>
              {state === 'active' ? `${step.label}…` : step.label}
            </Text>
          </HStack>
        );
      })}
      {slow ? (
        <Text size="xs" className="text-typography-700" accessibilityLiveRegion="polite">
          Still searching. You can enter your NPI below at any time.
        </Text>
      ) : null}
    </VStack>
  );
}
